import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TableProps } from 'antd';
import { EyeOutlined, ReloadOutlined, ScheduleOutlined, FileSearchOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { Bento, DataTable, PageHeader, TabsTruot, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Dialog } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { DemLuot } from '../components/van-hanh/DemLuot';
import { LichSuTroChuyen } from '../components/van-hanh/LichSuTroChuyen';
import { SuDung } from '../components/van-hanh/SuDung';
import { useLuotChay } from '../hooks/useVanHanh';
import type { QueryRun, SyncRun } from '../types';

function Luc({ iso }: { iso: string }) {
  const { locale } = useT();
  return <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">{ngayGioVN(iso, locale)}</span>;
}

/**
 * Nhật ký truy vết (`/tro-ly-ai/nhat-ky`, port `TracesView` native): mỗi chu kỳ đồng bộ và mỗi câu SQL đã đi qua bộ
 * canh cổng.
 *
 * - Hai nhịp chung một màn: câu mang tới đây thường là "hôm qua trợ lý trả lời sai", chỉ trả lời được khi thấy CẢ câu SQL
 *   đã chạy lúc ấy lẫn chu kỳ đồng bộ gần nhất trước đó.
 * - Lượt bị từ chối cũng nằm đây: chỉ hiện lượt chạy được thì nhật ký nói hệ thống chưa bao giờ từ chối gì — mà đó là
 *   phần đáng đọc nhất. Mã từ chối hiện nguyên văn vì nó là thứ tra cứu được.
 */
export function TracesView() {
  const { t, locale } = useT();
  // `?cv=<mã>` — đường dẫn tới đúng cuộc trò chuyện. Chỉ là giá trị ban đầu: đổi tab, chọn đoạn khác là quyền người dùng.
  const [params] = useSearchParams();
  const cvMoDau = params.get('cv');
  const [chiTiet, setChiTiet] = useState<QueryRun | null>(null);
  const runs = useLuotChay();

  const cotDongBo: TableProps<SyncRun>['columns'] = [
    { title: t('troLyAiVanHanhNhatKy.colLuc'), key: 'luc', width: 170, render: (_v, r) => <Luc iso={r.startedAt} /> },
    {
      title: t('troLyAiVanHanhNhatKy.colBang'),
      key: 'bang',
      width: 220,
      render: (_v, r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-content-primary">{r.table}</p>
          <p className="truncate text-2xs text-content-muted">{r.sourceId}</p>
        </div>
      ),
    },
    {
      title: t('troLyAiVanHanhNhatKy.colKetQua'),
      key: 'ketQua',
      width: 150,
      render: (_v, r) => (
        <Badge variant={r.status === 'ok' ? 'success' : r.status === 'skipped' ? 'neutral' : 'danger'}>
          {r.status === 'ok'
            ? r.mode === 'full'
              ? t('troLyAiVanHanhNhatKy.chepDayDu')
              : t('troLyAiVanHanhNhatKy.chepTiep')
            : r.status === 'skipped'
              ? t('troLyAiVanHanhNhatKy.boQua')
              : t('troLyAiVanHanhNhatKy.dungLai')}
        </Badge>
      ),
    },
    {
      title: t('troLyAiVanHanhNhatKy.colDong'),
      key: 'dong',
      width: 260,
      align: 'end',
      responsive: ['lg'],
      render: (_v, r) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {t('troLyAiVanHanhNhatKy.docGhi', { doc: soVN(r.rowsRead, locale), ghi: soVN(r.rowsWritten, locale) })}
          {r.rowsDeleted > 0 && t('troLyAiVanHanhNhatKy.xoaThem', { n: soVN(r.rowsDeleted, locale) })}
          <span className="ms-2 text-content-muted">
            {t('troLyAiVanHanhNhatKy.giayNgan', { so: soVN(Math.round(r.elapsedMs / 100) / 10, locale) })}
          </span>
        </span>
      ),
    },
    {
      title: t('troLyAiVanHanhNhatKy.colGhiChu'),
      key: 'ghiChu',
      render: (_v, r) =>
        r.message ? (
          <p className={r.status === 'ok' ? 'text-xs text-content-secondary' : 'text-xs text-danger-700'}>{r.message}</p>
        ) : (
          <span className="text-content-muted">{KHONG_CO}</span>
        ),
    },
  ];

  const cotCauHoi: TableProps<QueryRun>['columns'] = [
    { title: t('troLyAiVanHanhNhatKy.colLuc'), key: 'luc', width: 170, render: (_v, r) => <Luc iso={r.createdAt} /> },
    {
      title: t('troLyAiVanHanhNhatKy.colCauHoi'),
      key: 'cauHoi',
      width: 300,
      render: (_v, r) => (
        <div className="min-w-0 max-w-[300px]">
          <p className="truncate text-xs text-content-primary">
            {r.question ?? <span className="text-content-muted">{t('troLyAiVanHanhNhatKy.chayTrucTiep')}</span>}
          </p>
          <p className="truncate text-2xs text-content-muted">
            {r.sourceId ?? '?'}
            {r.actor && ` · ${r.actor}`}
          </p>
        </div>
      ),
    },
    {
      title: t('troLyAiVanHanhNhatKy.colKetQua'),
      key: 'ketQua',
      width: 130,
      render: (_v, r) => (
        <Badge variant={r.status === 'ok' ? 'success' : r.status === 'rejected' ? 'warning' : 'danger'}>
          {r.status === 'ok'
            ? t('troLyAiVanHanhNhatKy.soDong', { n: soVN(r.rowCount ?? 0, locale) })
            : r.status === 'rejected'
              ? t('troLyAiVanHanhNhatKy.biTuChoi')
              : t('troLyAiVanHanhNhatKy.chayHong')}
        </Badge>
      ),
    },
    {
      title: t('troLyAiVanHanhNhatKy.colChiTiet'),
      key: 'chiTiet',
      render: (_v, r) =>
        r.refusals?.length ? (
          <ul className="space-y-0.5">
            {r.refusals.map((x, i) => (
              <li key={i} className="text-xs text-warning-700">
                <span className="me-1 rounded-xs bg-warning-50 px-1 font-mono text-xs">{x.code}</span>
                {x.message}
              </li>
            ))}
          </ul>
        ) : (
          <code className="line-clamp-2 font-mono text-xs text-content-secondary">{r.sql}</code>
        ),
    },
    {
      // Một hành động duy nhất trên mọi dòng: giữ nút icon trần (ADR-UI-0005 — không bọc vào menu `⋮`).
      title: t('chung.thaoTac'),
      key: 'mo',
      width: 72,
      align: 'center',
      render: (_v, r) => (
        <Button
          size="sm"
          variant="ghost"
          icon={<EyeOutlined />}
          title={t('troLyAiVanHanhNhatKy.xemLuot')}
          aria-label={t('troLyAiVanHanhNhatKy.xemLuotCua', { luc: ngayGioVN(r.createdAt, locale) })}
          onClick={() => setChiTiet(r)}
        />
      ),
    },
  ];

  const bangCauHoi = (
    <Bento>
      <Tile>
        <TileHeader title={t('troLyAiVanHanhNhatKy.tieuDeCauHoi')} icon={<ThunderboltOutlined />} tone="primary" />
        {runs.error ? (
          <DaiLoi loi={runs.error} onThuLai={() => void runs.refetch()} />
        ) : (
          <DataTable<QueryRun>
            caption={t('troLyAiVanHanhNhatKy.tieuDeCauHoi')}
            columns={cotCauHoi}
            dataSource={runs.data?.queryRuns ?? []}
            loading={runs.isPending}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false }}
            locale={{ emptyText: t('troLyAiVanHanhNhatKy.rongCauHoi') }}
          />
        )}
      </Tile>
    </Bento>
  );

  const bangDongBo = (
    <Bento>
      <Tile>
        <TileHeader title={t('troLyAiVanHanhNhatKy.tieuDeDongBo')} icon={<ScheduleOutlined />} tone="teal" />
        {runs.error ? (
          <DaiLoi loi={runs.error} onThuLai={() => void runs.refetch()} />
        ) : (
          <DataTable<SyncRun>
            caption={t('troLyAiVanHanhNhatKy.tieuDeDongBo')}
            columns={cotDongBo}
            dataSource={runs.data?.syncRuns ?? []}
            loading={runs.isPending}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false }}
            locale={{ emptyText: t('troLyAiVanHanhNhatKy.rongDongBo') }}
          />
        )}
      </Tile>
    </Bento>
  );

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.nhatKyTruyVet') }]}
          titleIcon={<FileSearchOutlined className="text-primary-600" />}
          title={t('troLyAiVanHanhNhatKy.tieuDe')}
          subtitle={t('troLyAiVanHanhNhatKy.phuDe')}
          actions={
            <Button
              size="md"
              variant="secondary"
              icon={<ReloadOutlined />}
              loading={runs.isFetching}
              onClick={() => void runs.refetch()}
            >
              {t('troLyAiVanHanhNhatKy.taiLai')}
            </Button>
          }
        />

        <SuDung />

        {/* Chỉnh đệm là việc vận hành, người chỉnh cần thấy ngay nó ăn tới đâu — tỉ lệ trúng nằm trong chính khối này. */}
        <Bento>
          <DemLuot />
        </Bento>

        {/* Tab trò chuyện tự gọi dữ liệu và chỉ dựng khi mở — vai thường nhận 403 ở đó. */}
        <TabsTruot
          defaultActiveKey={cvMoDau ? 'chats' : 'queries'}
          items={[
            {
              key: 'queries',
              label: t('troLyAiVanHanhNhatKy.tabCauHoi', { n: soVN(runs.data?.queryRuns.length ?? 0, locale) }),
              children: bangCauHoi,
            },
            {
              key: 'chats',
              label: t('troLyAiVanHanhNhatKy.tabTroChuyen'),
              children: <LichSuTroChuyen cvMoDau={cvMoDau} />,
            },
            {
              key: 'syncs',
              label: t('troLyAiVanHanhNhatKy.tabDongBo', { n: soVN(runs.data?.syncRuns.length ?? 0, locale) }),
              children: bangDongBo,
            },
          ]}
        />
      </div>

      <Dialog
        open={chiTiet !== null}
        onClose={() => setChiTiet(null)}
        width={760}
        title={
          <div className="min-w-0">
            <p className="text-sm">{chiTiet?.question ?? t('troLyAiVanHanhNhatKy.cauLenhTrucTiep')}</p>
            <p className="text-xs font-normal text-content-muted">
              {chiTiet
                ? t('troLyAiVanHanhNhatKy.chiTietMo', {
                    luc: ngayGioVN(chiTiet.createdAt, locale),
                    nguon: chiTiet.sourceId ?? '?',
                  })
                : ''}
              {chiTiet?.elapsedMs != null && t('troLyAiVanHanhNhatKy.chiTietMs', { ms: soVN(chiTiet.elapsedMs, locale) })}
            </p>
          </div>
        }
      >
        <div className="space-y-4 pb-2">
          {chiTiet?.refusals?.length ? (
            <div className="space-y-2 rounded-lg border border-warning-500 bg-warning-50 p-3">
              <p className="text-xs font-medium text-warning-700">{t('troLyAiVanHanhNhatKy.congTuChoi')}</p>
              {chiTiet.refusals.map((x, i) => (
                <p key={i} className="text-xs text-warning-700">
                  <span className="me-1 font-mono text-xs">{x.code}</span>
                  {x.message}
                </p>
              ))}
            </div>
          ) : null}
          <div>
            <p className="mb-1 text-xs font-medium text-content-secondary">{t('troLyAiVanHanhNhatKy.cauLenh')}</p>
            <pre className="overflow-x-auto rounded-lg bg-subtle p-3 font-mono text-xs leading-relaxed text-content-primary">
              {chiTiet?.sql}
            </pre>
          </div>
          {chiTiet?.tables?.length ? (
            <div>
              <p className="mb-1 text-xs font-medium text-content-secondary">{t('troLyAiVanHanhNhatKy.bangDaDoc')}</p>
              <div className="flex flex-wrap gap-1">
                {chiTiet.tables.map((b) => (
                  <Badge key={b} variant="neutral">
                    {b}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}
